import { WORKSPACE_LABEL, SECTION_LABELS } from "@/config/uiConfig";
import type { ReactNode } from "react";
import { Pencil, X } from "lucide-react";
import { Button, LibraryImage } from "@nexus/ui";
import { useLocation, useNavigate } from "react-router-dom";

import { DashboardContextLine } from "@/components/dashboard/DashboardContextLine";
import type { DashboardContextEntry } from "@/lib/dashboard/contextEntries";
import { SectionLayout } from "@/components/layout/SectionLayout";
import type { BreadcrumbCrumb } from "@/components/layout/PageBreadcrumb";
import { useLiveNav } from "@/pages/live/liveNav";
import { useDashboardsNav } from "@/pages/workspace/dashboardsNav";
import { usePlacementNav } from "@/lib/dashboard/placementNav";
import { EventViewNavigation } from "@/pages/live/components/EventViewNavigation";
import { DashboardCollapseButton } from "@/components/dashboard/DashboardCollapseContext";
import type { EventWorkspaceView } from "@/pages/live/eventWorkspaceNavigation";
import { resolveDashboardHeaderImage } from "@/lib/dashboard/headerImages";

interface DashboardLayoutProps {
  title: string;
  /** Code of the image-library image shown beside the title. */
  headerImage?: string;
  /** What the dashboard is about, as text; shown after the context entries. */
  description?: string;
  /** What the dashboard is about (tenant, event…), stated under the title. */
  contextEntries?: readonly DashboardContextEntry[];
  documentTitle?: string;
  breadcrumbTrail?: readonly BreadcrumbCrumb[];
  eventView?: EventWorkspaceView;
  eventId?: string | null;
  editHref?: string;
  workspaceMenuItems?: ReactNode;
  collapseMenuEnabled?: boolean;
  refreshStatus?: ReactNode;
  /**
   * A generated report's edition bar in Post-event Reporting (shown above the
   * content), or the event scope control everywhere else (shown in the header).
   */
  pageContext?: ReactNode;
  onExitContext?: () => void;
  eventMonitorSelected?: boolean;
  eventMonitorPage?: boolean;
  eventOverviewPage?: boolean;
  eventProtectionPage?: boolean;
  contentVariant?: "page" | "dashboard";
  children: ReactNode;
}

/** Section placement of a dashboard route: which navigation and breadcrumb it belongs to. */
export function useDashboardPlacement() {
  const { pathname } = useLocation();
  const live = useLiveNav();
  const dashboards = useDashboardsNav();
  const reports = usePlacementNav("measurement");
  if (pathname.startsWith("/workspace/dashboards") || pathname.startsWith("/dashboards/"))
    return {
      section: "workspace",
      sectionTitle: WORKSPACE_LABEL,
      sidebarItems: dashboards,
    } as const;
  if (pathname.startsWith("/measurement/"))
    return {
      section: "measurement",
      sectionTitle: SECTION_LABELS.measurement,
      sidebarItems: reports,
    } as const;
  return { section: "live", sectionTitle: SECTION_LABELS.live, sidebarItems: live } as const;
}

export function DashboardLayout({
  title,
  headerImage: headerImageCode,
  description,
  contextEntries,
  documentTitle,
  breadcrumbTrail,
  eventView,
  eventId = null,
  editHref,
  workspaceMenuItems,
  collapseMenuEnabled = false,
  refreshStatus,
  pageContext,
  onExitContext,
  eventMonitorSelected = false,
  eventMonitorPage = false,
  eventOverviewPage = false,
  eventProtectionPage = false,
  contentVariant,
  children,
}: DashboardLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { section, sectionTitle, sidebarItems } = useDashboardPlacement();
  const protectionEntry =
    eventProtectionPage &&
    location.pathname === "/live/protection/events" &&
    !new URLSearchParams(location.search).has("event_id");
  const isReport = section === "measurement";

  const trail: readonly BreadcrumbCrumb[] | undefined =
    section === "workspace"
      ? [
          { label: WORKSPACE_LABEL, href: "/workspace/dashboards" },
          { label: "Dashboards", href: "/workspace/dashboards" },
          { label: title },
        ]
      : // A drilldown level (campaign, event…) brings its own full chain back
        // to the section root, so the parent stays reachable.
        breadcrumbTrail?.length
        ? breadcrumbTrail
        : section === "measurement"
          ? [
              { label: sectionTitle, href: `/${section}` },
              ...(isReport ? [{ label: "Reports", href: "/measurement" }] : []),
              { label: title },
            ]
          : undefined;

  const hasMenu = Boolean(eventView || workspaceMenuItems);
  const headerImage = resolveDashboardHeaderImage(headerImageCode);

  return (
    <SectionLayout
      sectionTitle={sectionTitle}
      sidebarItems={sidebarItems}
      title={title}
      titleVisual={
        headerImage ? (
          <LibraryImage code={headerImage} className="h-full text-secondary" />
        ) : undefined
      }
      description={
        contextEntries?.length ? (
          <span className="flex min-w-0 items-center gap-x-3">
            <DashboardContextLine entries={contextEntries} />
            {description ? (
              <span className="truncate border-l border-border pl-3">{description}</span>
            ) : null}
          </span>
        ) : (
          description
        )
      }
      documentTitle={documentTitle}
      breadcrumbTrail={trail}
      filters={isReport ? undefined : pageContext}
      filtersAfterTime={eventMonitorSelected}
      timeFirst={eventOverviewPage}
      actions={
        editHref ? (
          <Button
            variant="secondary"
            size="lg"
            className="whitespace-nowrap"
            aria-label="Edit dashboard"
            onClick={() => navigate(editHref)}
          >
            <Pencil className="size-4 shrink-0" aria-hidden />
            Edit
          </Button>
        ) : undefined
      }
      status={refreshStatus}
      viewActions={collapseMenuEnabled ? <DashboardCollapseButton /> : undefined}
      menuItems={
        hasMenu ? (
          <>
            {eventView ? <EventViewNavigation view={eventView} eventId={eventId} /> : null}
            {workspaceMenuItems}
          </>
        ) : undefined
      }
      closeAction={
        onExitContext ? (
          <button
            type="button"
            onClick={onExitContext}
            aria-label="Close Event Monitor and choose another event"
            title="Back to event selection"
            className="flex size-10 items-center justify-center rounded-lg border border-border bg-surface text-muted hover:bg-surface-elevated hover:text-primary focus-visible:ring-2 focus-visible:ring-focus"
          >
            <X className="size-4" aria-hidden />
          </button>
        ) : undefined
      }
      variant={
        contentVariant ??
        ((eventMonitorPage && !eventMonitorSelected) || protectionEntry ? "page" : "dashboard")
      }
      tenantScopeRail={eventMonitorPage || eventProtectionPage ? false : undefined}
      presentation
    >
      {isReport && pageContext ? <div className="density-page-header">{pageContext}</div> : null}
      {children}
    </SectionLayout>
  );
}
